/**
 * WaypointUpdater - Handles updating waypoint data in the XML DOM
 * 
 * Responsible for:
 * - Updating waypoint positions and rotations
 * - Updating via-point data
 * - Updating feature references
 * - Updating joint positions
 */
export class WaypointUpdater {
  /**
   * Update a waypoint's position
   * @param {Document} xmlDoc - The XML document
   * @param {number} index - Index of the waypoint to update
   * @param {Object} position - New position {x, y, z}
   */
  updatePosition(xmlDoc, index, position) {
    const poseElement = this.getWaypointElement(xmlDoc, index);
    const positionElement = poseElement.querySelector('Position[key="posePosition"]');
    
    if (position.x !== undefined) {
      positionElement.querySelector('Length[key="X"]').setAttribute('value', position.x.toString());
    }
    if (position.y !== undefined) {
      positionElement.querySelector('Length[key="Y"]').setAttribute('value', position.y.toString());
    }
    if (position.z !== undefined) {
      positionElement.querySelector('Length[key="Z"]').setAttribute('value', position.z.toString());
    }
  }

  /**
   * Update a waypoint's rotation
   * @param {Document} xmlDoc - The XML document
   * @param {number} index - Index of the waypoint to update
   * @param {Object} rotation - New rotation {rx, ry, rz}
   */
  updateRotation(xmlDoc, index, rotation) {
    const poseElement = this.getWaypointElement(xmlDoc, index);
    const rotationElement = poseElement.querySelector('Rotation[key="poseRotation"]');
    
    if (rotation.rx !== undefined) {
      rotationElement.querySelector('Angle[key="RX"]').setAttribute('value', rotation.rx.toString());
    }
    if (rotation.ry !== undefined) {
      rotationElement.querySelector('Angle[key="RY"]').setAttribute('value', rotation.ry.toString());
    }
    if (rotation.rz !== undefined) {
      rotationElement.querySelector('Angle[key="RZ"]').setAttribute('value', rotation.rz.toString());
    }
  }

  /**
   * Update a via-point's position
   * @param {Document} xmlDoc - The XML document
   * @param {number} waypointIndex - Index of the waypoint that has the via-point
   * @param {Object} position - New position {x, y, z}
   */
  updateViaPointPosition(xmlDoc, waypointIndex, position) {
    const viaPointElement = this.getViaPointElement(xmlDoc, waypointIndex);
    const positionElement = viaPointElement.querySelector('Position[key="posePosition"]');
    
    if (position.x !== undefined) {
      positionElement.querySelector('Length[key="X"]').setAttribute('value', position.x.toString());
    }
    if (position.y !== undefined) {
      positionElement.querySelector('Length[key="Y"]').setAttribute('value', position.y.toString());
    }
    if (position.z !== undefined) {
      positionElement.querySelector('Length[key="Z"]').setAttribute('value', position.z.toString());
    }
  }

  /**
   * Update a via-point's rotation
   * @param {Document} xmlDoc - The XML document
   * @param {number} waypointIndex - Index of the waypoint that has the via-point
   * @param {Object} rotation - New rotation {rx, ry, rz}
   */
  updateViaPointRotation(xmlDoc, waypointIndex, rotation) {
    const viaPointElement = this.getViaPointElement(xmlDoc, waypointIndex);
    const rotationElement = viaPointElement.querySelector('Rotation[key="poseRotation"]');
    
    if (rotation.rx !== undefined) {
      rotationElement.querySelector('Angle[key="RX"]').setAttribute('value', rotation.rx.toString());
    }
    if (rotation.ry !== undefined) {
      rotationElement.querySelector('Angle[key="RY"]').setAttribute('value', rotation.ry.toString());
    }
    if (rotation.rz !== undefined) {
      rotationElement.querySelector('Angle[key="RZ"]').setAttribute('value', rotation.rz.toString());
    }
  }

  /**
   * Update a waypoint's feature reference
   * @param {Document} xmlDoc - The XML document
   * @param {number} index - Index of the waypoint to update
   * @param {string} featureName - New feature name
   */
  updateFeature(xmlDoc, index, featureName) {
    const poseElement = this.getWaypointElement(xmlDoc, index);
    const parent = poseElement.closest('Contributed, suppressedNode');

    if (!parent) {
      throw new Error(`Could not find parent element for waypoint ${index}`);
    }

    // Find or create the feature data element
    let featureElement = parent.querySelector('data[key="feature"]');

    if (!featureElement) {
      featureElement = xmlDoc.createElement('data');
      featureElement.setAttribute('key', 'feature');
      parent.appendChild(featureElement);
    }

    featureElement.setAttribute('value', featureName);
  }

  /**
   * Update a waypoint's joint positions
   * @param {Document} xmlDoc - The XML document
   * @param {number} index - Index of the waypoint to update
   * @param {Array<number>} joints - Array of 6 joint angles in radians
   */
  updateJoints(xmlDoc, index, joints) {
    if (joints.length !== 6) {
      throw new Error('Joint array must contain exactly 6 values');
    }

    const poseElement = this.getWaypointElement(xmlDoc, index);
    const parent = poseElement.closest('Contributed, suppressedNode');

    if (!parent) {
      throw new Error(`Could not find parent element for waypoint ${index}`);
    }

    // Find or create the joint positions element
    let jointPositionsElement = parent.querySelector('JointPositions[key="way-point-joint"]');

    if (!jointPositionsElement) {
      jointPositionsElement = xmlDoc.createElement('JointPositions');
      jointPositionsElement.setAttribute('key', 'way-point-joint');
      parent.appendChild(jointPositionsElement);
    }

    // Update each joint position
    for (let i = 1; i <= 6; i++) {
      let jointElement = jointPositionsElement.querySelector(`JointPosition[key="[${i}]"]`);

      if (!jointElement) {
        jointElement = xmlDoc.createElement('JointPosition');
        jointElement.setAttribute('key', `[${i}]`);
        jointPositionsElement.appendChild(jointElement);
      }

      let angleElement = jointElement.querySelector('Angle[key="pos"]');

      if (!angleElement) {
        angleElement = xmlDoc.createElement('Angle');
        angleElement.setAttribute('key', 'pos');
        jointElement.appendChild(angleElement);
      }

      angleElement.setAttribute('value', joints[i - 1].toString());
    }
  }

  /**
   * Get waypoint Pose element by index
   * @param {Document} xmlDoc - The XML document
   * @param {number} index - Waypoint index
   * @returns {Element} The Pose element
   */
  getWaypointElement(xmlDoc, index) {
    const poses = xmlDoc.querySelectorAll('Pose[key="way-point"]');
    if (index < 0 || index >= poses.length) {
      throw new Error(`Invalid waypoint index: ${index}`);
    }
    return poses[index];
  }

  /**
   * Get via-point element for a waypoint
   * @param {Document} xmlDoc - The XML document
   * @param {number} waypointIndex - Waypoint index
   * @returns {Element} The via-point Pose element
   */
  getViaPointElement(xmlDoc, waypointIndex) {
    const waypointPoses = xmlDoc.querySelectorAll('Pose[key="way-point"]');
    if (waypointIndex < 0 || waypointIndex >= waypointPoses.length) {
      throw new Error(`Invalid waypoint index: ${waypointIndex}`);
    }
    
    const waypointElement = waypointPoses[waypointIndex];
    const parent = waypointElement.closest('Contributed, suppressedNode');
    const viaPointElement = parent?.querySelector('Pose[key="via-point"]');
    
    if (!viaPointElement) {
      throw new Error(`No via-point found for waypoint ${waypointIndex}`);
    }
    
    return viaPointElement;
  }
}
